/**
 * Training Center XML (TCX) Serializer Domain Module
 * Generates standards-compliant Garmin/Strava TCX XML for workout activities, laps, and heart rate trackpoints.
 * 
 * @module integrations/tcx
 */

/**
 * Maps workout type to Garmin TCX Sport attribute.
 * @param {string} [type] - Workout type (e.g. 'Run', 'Cycle', 'Hyrox')
 * @returns {'Running'|'Biking'|'Other'}
 */
export function mapWorkoutTypeToSport(type) {
  if (!type) return 'Other';
  const norm = String(type).trim().toLowerCase();
  if (norm === 'run' || norm === 'running') return 'Running';
  if (norm === 'cycle' || norm === 'cycling' || norm === 'ride' || norm === 'biking') return 'Biking';
  return 'Other';
}

/**
 * Escapes characters for safe inclusion in XML text nodes or attributes.
 * @param {string} [str]
 * @returns {string}
 */
export function escapeXml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Serializes a workout into a valid Training Center Database (TCX) XML document string.
 *
 * @param {Object} workout - The workout object to serialize.
 * @param {number|string|Date} [workout.timestamp] - Start timestamp of workout.
 * @param {string} [workout.type] - Activity type ('Run', 'Hyrox', 'Cycle', etc.).
 * @param {number} [workout.duration] - Overall duration in minutes.
 * @param {number} [workout.calories] - Estimated total calories burned.
 * @param {number} [workout.distance] - Total distance in km or meters.
 * @param {number} [workout.avgHeartrate] - Average heart rate in bpm.
 * @param {number} [workout.maxHeartrate] - Peak heart rate in bpm.
 * @param {string} [workout.deviceName] - Device creator string.
 * @param {Array<Object>} [workout.stages] - Structured workout stages/splits.
 * @param {Object} [options={}] - Serialization options.
 * @param {string} [options.sportOverride] - Overrides the mapped sport attribute.
 * @param {string} [options.creatorNameOverride] - Overrides the device creator name.
 * @param {number} [options.intervalSecs] - Trackpoint sample interval in seconds.
 * @param {boolean} [options.includeExtensions=true] - Whether to include station extensions in lap metadata.
 * @returns {string} XML string
 */
export function generateTCX(workout, options = {}) {
  if (!workout || typeof workout !== 'object') {
    throw new TypeError('generateTCX requires a valid workout object');
  }

  const rawDate = workout.timestamp || Date.now();
  const startDate = new Date(rawDate);
  const startIso = isNaN(startDate.getTime()) ? new Date().toISOString() : startDate.toISOString();

  const rawSport = options.sportOverride || mapWorkoutTypeToSport(workout.type);
  const sport = escapeXml(rawSport);
  const creatorName = escapeXml(options.creatorNameOverride || workout.deviceName || 'RoxKit Watch');

  let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  xml += `<TrainingCenterDatabase xmlns="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">\n`;
  xml += `  <Activities>\n`;
  xml += `    <Activity Sport="${sport}">\n`;
  xml += `      <Id>${startIso}</Id>\n`;

  let currentMs = startDate.getTime();
  const stages = Array.isArray(workout.stages) && workout.stages.length > 0 ? workout.stages : null;
  const rawAvgHr = Number.isFinite(workout.avgHeartrate) && workout.avgHeartrate > 0 ? Math.round(workout.avgHeartrate) : 140;
  const overallAvgHr = Math.max(30, Math.min(240, rawAvgHr));
  const rawMaxHr = Number.isFinite(workout.maxHeartrate) && workout.maxHeartrate > 0 ? Math.round(workout.maxHeartrate) : Math.min(220, overallAvgHr + 20);
  const overallMaxHr = Math.max(overallAvgHr, Math.min(240, rawMaxHr));

  if (stages) {
    stages.forEach((stage, idx) => {
      const durMin = Number.isFinite(stage.durationMin) ? Math.max(0, stage.durationMin) : 0;
      const durSec = Number.isFinite(stage.durationSec) ? Math.max(0, stage.durationSec) : 0;
      const stageSecs = Math.max(10, Math.round(durMin * 60 + durSec));
      const stageStartIso = new Date(currentMs).toISOString();

      const stageAvgHr = Number.isFinite(stage.avgHr) && stage.avgHr > 0 ? Math.round(stage.avgHr) : overallAvgHr;
      const stageMaxHr = Math.min(220, Math.max(stageAvgHr + 10, overallMaxHr));
      const stageCalories = Math.max(0, Math.round((workout.calories || 0) / stages.length));

      let stageDistMeters = 0;
      const stType = String(stage.type || '').toLowerCase();
      const stName = String(stage.stationName || '').toLowerCase();
      const isRun = stType === 'run' || stName.includes('run');

      if (isRun) {
        if (Number.isFinite(stage.distance) && stage.distance > 0) {
          stageDistMeters = stage.distance > 50 ? Math.round(stage.distance) : Math.round(stage.distance * 1000);
        } else {
          stageDistMeters = 1000; // Default 1.0 km per HYROX running split
        }
      } else if (Number.isFinite(stage.distance) && stage.distance > 0) {
        stageDistMeters = stage.distance > 50 ? Math.round(stage.distance) : Math.round(stage.distance * 1000);
      }

      xml += `      <Lap StartTime="${stageStartIso}">\n`;
      xml += `        <TotalTimeSeconds>${stageSecs}</TotalTimeSeconds>\n`;
      xml += `        <DistanceMeters>${stageDistMeters}</DistanceMeters>\n`;
      xml += `        <Calories>${stageCalories}</Calories>\n`;
      xml += `        <AverageHeartRateBpm><Value>${stageAvgHr}</Value></AverageHeartRateBpm>\n`;
      xml += `        <MaximumHeartRateBpm><Value>${stageMaxHr}</Value></MaximumHeartRateBpm>\n`;
      xml += `        <Intensity>Active</Intensity>\n`;
      xml += `        <TriggerMethod>Manual</TriggerMethod>\n`;

      if (options.includeExtensions !== false && (stage.stationName || stage.type)) {
        const stageLabel = escapeXml(stage.stationName || stage.type || `Stage ${idx + 1}`);
        xml += `        <Notes>${stageLabel}</Notes>\n`;
      }

      xml += `        <Track>\n`;

      const intervalSecs = options.intervalSecs || 5;
      const totalPoints = Math.max(2, Math.floor(stageSecs / intervalSecs));
      for (let p = 0; p < totalPoints; p++) {
        const ptTime = new Date(currentMs + p * intervalSecs * 1000).toISOString();
        const progress = p / totalPoints;
        const hrVariation = Math.sin(p * 0.5) * 4;
        const hrVal = Math.round(Math.min(stageMaxHr, Math.max(60, stageAvgHr - 5 + (progress * 10) + hrVariation)));

        xml += `          <Trackpoint>\n`;
        xml += `            <Time>${ptTime}</Time>\n`;
        xml += `            <HeartRateBpm><Value>${hrVal}</Value></HeartRateBpm>\n`;
        xml += `          </Trackpoint>\n`;
      }

      xml += `        </Track>\n`;
      xml += `      </Lap>\n`;

      currentMs += stageSecs * 1000;
    });
  } else {
    // Single lap fallback for workouts without structured stages
    const durMins = Number.isFinite(workout.duration) && workout.duration > 0 ? workout.duration : 30;
    const totalSecs = Math.max(60, Math.round(durMins * 60));
    const calories = Math.max(0, Math.round(workout.calories || 0));

    let distMeters = 0;
    if (Number.isFinite(workout.distance) && workout.distance > 0) {
      distMeters = workout.distance > 50 ? Math.round(workout.distance) : Math.round(workout.distance * 1000);
    }

    xml += `      <Lap StartTime="${startIso}">\n`;
    xml += `        <TotalTimeSeconds>${totalSecs}</TotalTimeSeconds>\n`;
    xml += `        <DistanceMeters>${distMeters}</DistanceMeters>\n`;
    xml += `        <Calories>${calories}</Calories>\n`;
    xml += `        <AverageHeartRateBpm><Value>${overallAvgHr}</Value></AverageHeartRateBpm>\n`;
    xml += `        <MaximumHeartRateBpm><Value>${overallMaxHr}</Value></MaximumHeartRateBpm>\n`;
    xml += `        <Intensity>Active</Intensity>\n`;
    xml += `        <TriggerMethod>Manual</TriggerMethod>\n`;
    xml += `        <Track>\n`;

    const intervalSecs = options.intervalSecs || 10;
    const totalPoints = Math.max(2, Math.floor(totalSecs / intervalSecs));
    for (let p = 0; p < totalPoints; p++) {
      const ptTime = new Date(currentMs + p * intervalSecs * 1000).toISOString();
      const progress = p / totalPoints;
      const hrVariation = Math.sin(p * 0.3) * 6;
      const hrVal = Math.round(Math.min(overallMaxHr, Math.max(60, overallAvgHr - 10 + (progress * 15) + hrVariation)));

      xml += `          <Trackpoint>\n`;
      xml += `            <Time>${ptTime}</Time>\n`;
      xml += `            <HeartRateBpm><Value>${hrVal}</Value></HeartRateBpm>\n`;
      xml += `          </Trackpoint>\n`;
    }

    xml += `        </Track>\n`;
    xml += `      </Lap>\n`;
  }

  xml += `      <Creator>\n`;
  xml += `        <Name>${creatorName}</Name>\n`;
  xml += `      </Creator>\n`;
  xml += `    </Activity>\n`;
  xml += `  </Activities>\n`;
  xml += `</TrainingCenterDatabase>`;

  return xml;
}
